import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from './static/components/header/header.component';
import { MsalBroadcastService, MsalModule, MsalService } from '@azure/msal-angular';
import { AuthService } from './static/services/auth.service';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, interval, Subscription, takeUntil } from 'rxjs';
import { HttpService } from '@static/services/http.service';
import { environment } from '@environments/environment';
import { SnackService } from '@static/services/snack.service';
import { StorageService } from '@static/services/storage.service';
import { KEY_VERSION} from '@static/storage-keys';
import { ISiteInit } from '@static/models/user';
import { Helper } from '@static/helpers';
import { Title } from '@angular/platform-browser';
import { ToastComponent } from '@static/components/toast/toast.component';
import { MatDialog } from '@angular/material/dialog';
import { IRecentChangesData, RecentChangesComponent } from '@core/changelog/recent-changes/recent-changes.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ExtensionService, TExtensions } from '@shared/services/extension.service';
import { SiteAdminService } from '@core/site-admin/services/site-admin.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    ToastComponent,
    MsalModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, OnDestroy {
  private _authService: AuthService = inject(AuthService);
  private _storageService: StorageService = inject(StorageService);
  private _msalService: MsalService = inject(MsalService);
  private _msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);
  private _httpService: HttpService = inject(HttpService);
  private _snackService: SnackService = inject(SnackService);
  private _titleService: Title = inject(Title);
  private _dialog: MatDialog = inject(MatDialog);
  private _siteAdminService: SiteAdminService = inject(SiteAdminService);

  // Load the extension service
  private _extensionService: ExtensionService = inject(ExtensionService);

  title = 'timesheet-ui';

  public init: boolean;
  private _settingsInterval: Subscription;

  // Record user settings on navigations
  @HostListener('document:visibilitychange', []) onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      // We can't query for a valid access token on browser close (can't make async requests)
      // so we just have to use the latest value here, with periodic checks it should
      // be valid (not expired).
      if (this._storageService.hasChanges) {
        this._storageService.commitChanges(Promise.resolve(this._authService.accessToken.value));
      }
    }
  }

  constructor() {
    this.init = false;

    // Set tab title for easier identification of mode
    if (environment.production) this._titleService.setTitle('Ctrl key');
    if (!environment.production && !window.location.hostname.includes('localhost'))
      this._titleService.setTitle('Ctrl key - Development');
    if (!environment.production && window.location.hostname.includes('localhost'))
      this._titleService.setTitle('Ctrl key - Local');

    // Periodically save settings (if updated)
    this._settingsInterval = interval(15 * 1000).subscribe(() => {
      // Periodically query for a valid query token while submitting user settings
      if (this._storageService.hasChanges) this._storageService.commitChanges(this._authService.checkToken());
    });
  }

  ngOnInit(): void {
    this._msalService.handleRedirectObservable().subscribe();

    this._authService.setLoginDisplay();

    this._msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._authService.destroying)
      )
      .subscribe(() => {
        this._authService.setLoginDisplay();
        this._authService.checkAndSetActiveAccount();
      });

    // Initialise WS auth
    this._httpService.getP<ISiteInit>(`${environment.backendUri}/authinit`)
      .then((res: ISiteInit) => {
        const userRes = res.user;

        // Previous version
        const initial = this._storageService.loadData('version', false);
        // Set the version
        environment.version = userRes.version;

        if (initial && initial !== environment.version) {
          this._dialog.open(RecentChangesComponent, {
            data: {
              lastVersion: initial,
              currentVersion: environment.version,
            } as IRecentChangesData
          });
        }

        this._siteAdminService.maxUsers = res.maxUsers;

        // Load saved user settings into local storage
        if (Helper.isDefined(userRes.settings) && userRes.settings !== '')
          this._storageService.loadStorageData(userRes.settings);

        this._storageService.saveData(KEY_VERSION, environment.version, true, false);

        // Set global extension allowance
        Object.entries(res.extEnabled).forEach(([key, val]) => {
          const recordKey = key.replace('ext_', '');
          this._extensionService.allowedExtensions.set(recordKey as TExtensions, val);
        });

        this.init = true;
      }).catch((err: HttpErrorResponse) => {
        if (!err.ok && err.status === 403) {
          this._snackService.openErrorSnack('You do not have permissions to this site', err, true);
        }
        else if (!err.ok && err.status === 0) this._snackService.openErrorSnack('Unable to connect to server', err, true);
        else this._snackService.openErrorSnack('Failed to load user profile', err, true);
      });
  }

  ngOnDestroy(): void {
    if (this._settingsInterval) this._settingsInterval.unsubscribe();
  }
}
